import React, { useEffect, useState } from "react";
import { Form, Input, Select, Switch, InputNumber, Button, Upload, message, DatePicker } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faArrowLeft, faImage } from "@fortawesome/free-solid-svg-icons";
import bannerApi from "../../../api/bannerApi";
import { AudienceFields, audiencePayload } from "../../../components/audience-fields";
import BannerPopupPreview from "./BannerPopupPreview";
import dayjs from "dayjs";

const API_IMG_URL = process.env.REACT_APP_API_URL?.replace("/api/v1", "") || "";

function BannerForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewMeta, setPreviewMeta] = useState({
        title: "",
        linkUrl: "",
        linkType: "internal",
        displayTarget: "all",
    });

    useEffect(() => {
        if (isEdit) loadBanner();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadBanner = async () => {
        try {
            const { data } = await bannerApi.getAll();
            const banner = (data.data || []).find((b) => b.bannerId === Number(id));
            if (!banner) {
                message.error("Banner không tồn tại");
                navigate("/banner");
                return;
            }
            setPreviewUrl(`${API_IMG_URL}${banner.imagePath}`);
            const fields = {
                title: banner.title,
                linkUrl: banner.linkUrl,
                linkType: banner.linkType,
                displayTarget: banner.displayTarget,
                audience: banner.audience || "all",
                audienceDays: banner.audienceDays ?? null,
                isActive: banner.isActive,
                priority: banner.priority,
                showOncePerDay: banner.showOncePerDay,
                startTime: banner.startTime ? dayjs.unix(banner.startTime) : null,
                endTime: banner.endTime ? dayjs.unix(banner.endTime) : null,
            };
            form.setFieldsValue(fields);
            setPreviewMeta({
                title: banner.title,
                linkUrl: banner.linkUrl,
                linkType: banner.linkType,
                displayTarget: banner.displayTarget,
            });
        } catch {
            message.error("Không thể tải thông tin banner");
        }
    };

    const handleValuesChange = (_, all) => {
        setPreviewMeta({
            title: all.title || "",
            linkUrl: all.linkUrl || "",
            linkType: all.linkType || "internal",
            displayTarget: all.displayTarget || "all",
        });
    };

    const handleFileChange = ({ fileList: newList }) => {
        setFileList(newList);
        if (newList.length > 0 && newList[0].originFileObj) {
            setPreviewUrl(URL.createObjectURL(newList[0].originFileObj));
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("linkUrl", values.linkUrl || "");
            formData.append("linkType", values.linkType || "internal");
            formData.append("displayTarget", values.displayTarget || "all");
            const audience = audiencePayload(values);
            formData.append("audience", audience.audience);
            formData.append("audienceDays", audience.audienceDays);
            formData.append("isActive", values.isActive !== false ? "true" : "false");
            formData.append("priority", String(values.priority || 0));
            formData.append("showOncePerDay", values.showOncePerDay !== false ? "true" : "false");
            if (values.startTime) formData.append("startTime", String(values.startTime.unix()));
            if (values.endTime) formData.append("endTime", String(values.endTime.unix()));

            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append("image", fileList[0].originFileObj);
            } else if (!isEdit) {
                message.error("Vui lòng chọn ảnh banner");
                setLoading(false);
                return;
            }

            if (isEdit) {
                await bannerApi.update(Number(id), formData);
                message.success("Cập nhật banner thành công");
            } else {
                await bannerApi.create(formData);
                message.success("Tạo banner thành công");
            }
            navigate("/banner");
        } catch (error) {
            message.error(error.response?.data?.message || "Có lỗi xảy ra");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 w-full">
            <div className="flex items-center gap-3 mb-5">
                <Button icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/banner")} />
                <h1 className="text-2xl font-bold">{isEdit ? "Sửa Banner" : "Tạo Banner mới"}</h1>
            </div>

            <div className="flex gap-8">
                {/* ── Form ── */}
                <div className="flex-1 max-w-xl">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        onValuesChange={handleValuesChange}
                        initialValues={{ linkType: "internal", displayTarget: "all", audience: "all", isActive: true, priority: 0, showOncePerDay: true }}
                    >
                        <Form.Item label="Tiêu đề" name="title" rules={[{ required: true, message: "Nhập tiêu đề banner" }]}>
                            <Input placeholder="VD: Khuyến mãi Tết 2026" />
                        </Form.Item>

                        <Form.Item label="Ảnh banner">
                            <div className="flex items-center gap-3">
                                {/* Inline thumbnail */}
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt="preview"
                                        style={{ width: 108, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }}
                                    />
                                ) : (
                                    <div
                                        style={{ width: 108, height: 72, borderRadius: 6, border: "1px dashed #d1d5db", background: "rgb(var(--surface-sunken))" }}
                                        className="flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faImage} className="text-content-3 text-2xl" />
                                    </div>
                                )}
                                <Upload
                                    maxCount={1}
                                    fileList={fileList}
                                    onChange={handleFileChange}
                                    beforeUpload={() => false}
                                    accept="image/*"
                                    showUploadList={false}
                                >
                                    <Button icon={<FontAwesomeIcon icon={faUpload} className="mr-2" />}>
                                        {previewUrl ? "Thay ảnh" : "Chọn ảnh"}
                                    </Button>
                                </Upload>
                            </div>
                        </Form.Item>

                        <Form.Item
                            label="Link URL"
                            name="linkUrl"
                            extra="Internal nên dùng đường dẫn nội bộ, vd: /post/40, /link-converter, /vouchers"
                        >
                            <Input placeholder="VD: /post/40 hoặc https://longhousee.com/post/40" />
                        </Form.Item>

                        <div className="flex gap-4">
                            <Form.Item
                                label="Loại link"
                                name="linkType"
                                className="w-1/2"
                                extra="Internal: mở màn hình trong app (vd bài viết). External: mở trình duyệt."
                            >
                                <Select>
                                    <Select.Option value="internal">Internal (trong app/web)</Select.Option>
                                    <Select.Option value="external">External (mở trình duyệt)</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="Hiển thị trên" name="displayTarget" className="w-1/2">
                                <Select>
                                    <Select.Option value="all">Tất cả (Web + App)</Select.Option>
                                    <Select.Option value="web">Chỉ Web</Select.Option>
                                    <Select.Option value="app">Chỉ App</Select.Option>
                                </Select>
                            </Form.Item>
                        </div>

                        <AudienceFields />

                        <div className="flex gap-4">
                            <Form.Item label="Thứ tự ưu tiên" name="priority" className="w-1/3">
                                <InputNumber min={0} className="w-full" placeholder="0 = cao nhất" />
                            </Form.Item>
                            <Form.Item label="Hiện 1 lần/ngày" name="showOncePerDay" valuePropName="checked" className="w-1/3">
                                <Switch />
                            </Form.Item>
                            <Form.Item label="Active" name="isActive" valuePropName="checked" className="w-1/3">
                                <Switch />
                            </Form.Item>
                        </div>

                        <div className="flex gap-4">
                            <Form.Item label="Bắt đầu hiện" name="startTime" className="w-1/2">
                                <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" placeholder="Để trống = luôn hiện" />
                            </Form.Item>
                            <Form.Item label="Kết thúc" name="endTime" className="w-1/2">
                                <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" placeholder="Không giới hạn" />
                            </Form.Item>
                        </div>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading} className="mr-2">
                                {isEdit ? "Cập nhật" : "Tạo Banner"}
                            </Button>
                            <Button onClick={() => navigate("/banner")}>Hủy</Button>
                        </Form.Item>
                    </Form>
                </div>

                {/* ── Live Preview (hidden on small screens) ── */}
                <div className="hidden lg:block w-60">
                    <BannerPopupPreview
                        title={previewMeta.title}
                        imageUrl={previewUrl}
                        linkUrl={previewMeta.linkUrl}
                        linkType={previewMeta.linkType}
                        displayTarget={previewMeta.displayTarget}
                    />
                </div>
            </div>
        </div>
    );
}

export default BannerForm;
